import React, { memo } from "react";
import {
    Card,
    CardBody,
    CardTitle,
    ModalBody,
    Button,
    Badge,
} from "reactstrap";
import { FaPhone } from "react-icons/fa";
import Drawer from "../../../Drawer";
import AgentInfo from "../../../AgentInfo";
import Contact from "../../../Contact";
import useToggle from "../../../../hooks/useToggle";
import { useTrackEvent } from "../../../../hooks/useTrackEvent";
import { isStringOrNumber } from "../../../../support/helpers";

/**
 * Renders an agent's card with contact information and actions.
 *
 * @param {Object} agent - The agent object containing details such as name, picture, title, and contact information.
 * @param {Array} statesServiced - The array of states serviced by the agent.
 * @param {Array} nicheItems - The array of niche items associated with the agent.
 * @param {Object} filterItems - The object containing filter items for the card.
 * @param {Function} handleClick - The function to handle click events.
 * @param {string} stateCode - The code for the state.
 * @return {JSX.Element} The rendered agent's card component.
 */
const AgentsCard = memo(
    ({
        agent,
        statesServiced,
        nicheItems,
        filterItems,
        handleClick,
        stateCode,
        isEntitled,
    }) => {
        const {
            dIAgentPic,
            isActive,
            agentName,
            websitePhone,
            title,
            titles,
            brokerageName,
            brokerAgentStates,
            brokerAgentNicheItems,
            brokerAgentNicheItemsPrimary,
            id: agentId,
            uRI4Agent,
        } = agent;
        const { trackEvent, trackGTM } = useTrackEvent();
        const [modalIsOpen, toggleModal] = useToggle(false);
        const agentNicheItems = (
            brokerAgentNicheItemsPrimary ||
            brokerAgentNicheItems ||
            []
        ).map((nicheItemId) =>
            nicheItems.find((ni) => ni.nicheItemId === nicheItemId)
        );
        const stateId = filterItems?.stateId || brokerAgentStates[0];
        const nicheItemByState = agentNicheItems.find(
            (ni) => ni?.stateId === stateId
        );
        const selectedState =
            statesServiced &&
            statesServiced.find((state) => state?.stateId === stateId);
        const { stateName } = selectedState || {};
        const { displayName: nicheName, nicheItemPath } =
            nicheItemByState || {};
        const displayName = filterItems?.nicheItemId
            ? undefined
            : filterItems?.stateId
            ? nicheName
            : stateName;

        const toCamelCase = (str) => {
            try {
                return str.replace(/^([A-Z])|[\s-_](\w)/g, (m, p1, p2, o) =>
                    p2 ? p2.toUpperCase() : p1.toLowerCase()
                );
            } catch {
                return str;
            }
        };

        const displayTitle =
            stateCode && titles && titles?.[toCamelCase(stateCode)]
                ? titles?.[toCamelCase(stateCode)]?.title
                : title;

        const handleContactClick = () => {
            toggleModal();
            trackGTM({
                event: `formContact`,
                action: "click",
                category: "contact",
                type: "Agent",
                location: "States Agents Card",
                ...agent,
            });
        };

        return (
            <>
                <div className="p-2 bg-light h-100">
                    <Card className="h-100 m-1 border-0 shadow-sm rounded-0 position-relative p-3">
                        {isEntitled && !isActive ? (
                            <div className="d-flex justify-content-end mb-2">
                                <Badge size="sm" color="warning">
                                    Inactive
                                </Badge>
                            </div>
                        ) : null}
                        <a
                            href={`/${nicheItemPath}/${uRI4Agent}`}
                            onClick={(e) => {
                                e.preventDefault();
                                handleClick(agentId);
                            }}
                        >
                            <CardTitle tag="h4" className="title text-dark">
                                {agentName}
                            </CardTitle>
                        </a>
                        <div className="d-flex">
                            <a
                                href={`/${nicheItemPath}/${uRI4Agent}`}
                                className="me-3"
                                style={{ flex: "0 0 120px" }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleClick(agentId);
                                }}
                            >
                                <img
                                    src={
                                        dIAgentPic.imageUrl ||
                                        "/assets/no-image-found-360x260.png"
                                    }
                                    onError={(e) =>
                                        (e.target.src =
                                            "/no-image-found-360x260.png")
                                    }
                                    loading="lazy"
                                    alt={agentName}
                                    className="rounded-0 h-auto w-100"
                                />
                            </a>
                            <CardBody className="p-0 text-dark d-flex flex-column">
                                <div>
                                    {displayName && (
                                        <strong className="d-block">
                                            {displayName}
                                        </strong>
                                    )}
                                    {displayTitle && (
                                        <strong className="d-block">
                                            {displayTitle}
                                        </strong>
                                    )}
                                    {isStringOrNumber(websitePhone) && (
                                        <Button
                                            color="link"
                                            className="d-flex align-items-cetner m-0 px-0"
                                            href={`tel:${websitePhone}`}
                                            onClick={() => {
                                                trackGTM({
                                                    event: `phoneClick`,
                                                    action: "click",
                                                    type: "Agent",
                                                    location:
                                                        "States Agents Card",
                                                    category: "contact",
                                                    button_label: websitePhone,
                                                    value: websitePhone,
                                                    ...agent,
                                                });
                                                trackEvent("ACTIONS", {
                                                    CATEGORY: "event",
                                                    ASSOCIATE:
                                                        "States Agents Card Phone",
                                                    ACTION: "click",
                                                    SOURCE_INFO: {
                                                        eventLabel: agentName,
                                                        eventValue:
                                                            websitePhone,
                                                    },
                                                });
                                            }}
                                        >
                                            <div>
                                                <FaPhone
                                                    className="me-2"
                                                    size="12"
                                                    style={{
                                                        marginTop: "-4px",
                                                    }}
                                                />
                                                {websitePhone.toString()}
                                            </div>
                                        </Button>
                                    )}
                                </div>
                                <Button
                                    color="primary"
                                    className="w-100 inverse mt-auto px-0"
                                    onClick={() => handleClick(agentId)}
                                >
                                    About Me
                                </Button>
                                <Button
                                    color="primary"
                                    className="w-100 inverse mt-1 px-0"
                                    onClick={() => handleContactClick()}
                                >
                                    Contact Me
                                </Button>
                            </CardBody>
                        </div>
                    </Card>
                </div>
                <Drawer
                    isOpen={modalIsOpen}
                    toggle={() => toggleModal()}
                    headerText={`Contact ${agentName}`}
                >
                    <ModalBody>
                        <AgentInfo
                            agentName={agentName}
                            agentId={agentId}
                            imagePath={dIAgentPic?.imageUrl}
                            imageAlt={agentName}
                            phone={websitePhone}
                            brokerageName={brokerageName}
                        />
                        <Contact agentId={agentId} />
                    </ModalBody>
                </Drawer>
            </>
        );
    }
);

export default AgentsCard;
